import SharedPreferences from "../../constants/SharedPreferences";
import {
  getErrorMessage,
  generateBCryptSalt,
  saveInStorage,
  getFromStorage,
  logApiResponse,
  getStorageEncryptionKey,
  getFromInternalStorage,
  getEncryptionCurveFromStorage,
  getSigningCurveFromStorage,
  generateHMacHash,
  logoutViaServer,
  clearStorage,
} from "../../util/Utility";
import {
  getNonAuthRestClient,
  getTwoFARestClient,
  getSetTwoFARestClient,
} from "../RestUtility";
import {
  aeadDecryptWithAssociatedData,
  aeadEncryptWithAssociatedData,
  aeadEncryptWithPasscode,
  generateAesKey,
} from "../../crypto/AuthenticatedEncryption";
import {
  exportEccPrivateKey,
  exportEccPublicKey,
  generateEccKeyPair,
  generateEccSigningKeyPair,
} from "../../crypto/EccEncryption";
import BayunError from "../../constants/BayunError";
import Constants from "../../constants/Constants";
import { AuthenticateResponse } from "../../BayunCore";
import { encryptAsymmetric } from "../../util/AsymmetricEncryption";
import {
  appendSignatureAndMetadata,
  getAdminPrivateKeyKekContext,
  getAdminSigningPrivateKeyKekContext,
  getBackdoorSigningPrivateKeyKekContext,
  getAdminPublicKeyContext,
  getBackdoorPrivateKeyPartKekContext,
  getBackdoorPublicKeyContext,
  getBackdoorPublicKeyContextLayer2,
  getOrgKeyContext,
  getOrgKeyKekAdminOrgContext,
  getOrgKeyKekMemberLockboxContext,
  getOrgPrivateKeyContext,
  getOrgPrivateKeyKekContext,
  getOrgPublicKeyContext,
  getOrgPublicKeyContextLayer2,
  getMemberAppPublicKeyContext,
  getMemberAppPublicKeyContextLayer2,
  getMemberKeyKekContext,
  getMemberPrivateKeyInnerKekContext,
  getMemberPrivateKeyKekContext,
  getMemberPublicKeyContext,
  getMemberSigningPrivateKeyKekContext,
  getMemberSigningPublicKeyContext,
  getMemberSigningPublicKeyContextLayer2,
  getGhostMemberIdContext,
  getGhostMemberPublicKeyContext,
  getMessageFromSignatureString,
  getUserPrivateKeyOuterKekContext,
  getOrgAppApprovalContext,
  signData,
  signDataAndAppendSignatureWithMetadata,
} from "../../util/SignatureUtil";
import {
  derivePbkdf2Encoded,
  derivePbkdf2Raw,
  encodeKey,
} from "../../crypto/PBKDF2";
import SigningPublicKeyTags from "../../constants/SigningPublicKeyTags";
import { getLMSStatisticsKey } from "../../manager/CryptManager";
// import { deserializeThrift, serializeThrift } from "../../thrift/ThriftFormatter";
// import { MemberPublicKeyMetadata } from "../../thrift/MemberPublicKeyMetadata_types";
import { getRefreshToken } from "./internal/refreshToken";
import { getOrgSettingsRequest } from "./internal/orgSettings";
import { getMemberClientData } from "./memberApi";

const MEMBER_AUTHORIZATION_PENDING_MESSAGE =
  "The member is not authorized. Authorize the member first, then log in again.";
const MEMBER_AUTHORIZED_RELOGIN_MESSAGE =
  "The member is authorized. Log in again to continue.";

async function logoutSession(sessionId) {
  try {
    await logoutViaServer(sessionId);
  } catch (error) {
    console.error("Logout error:", error);
  } finally {
    clearStorage(sessionId);
  }
}

/**
 * Invalidates the just-created session (server + local) and notifies the host
 * that authorization is pending. authorizeMember does not need session storage;
 * the host must authorize the member and then log in again.
 */
async function logoutThenAuthorizeMemberCallback(
  sessionId,
  orgName,
  orgMemberId,
  authorizeMemberCallback,
) {
  await logoutSession(sessionId);

  authorizeMemberCallback({
    sessionId,
    authenticationResponse: AuthenticateResponse.AUTHORIZATION_PENDING,
    orgName,
    orgMemberId,
    error: BayunError.ERROR_MEMBER_AUTHORIZATION_IS_PENDING,
    message: MEMBER_AUTHORIZATION_PENDING_MESSAGE,
  });
}

export async function createMemberData(
  sessionId,
  orgName,
  orgMemberId,
  passcode,
  appId,
  appSecret,
  appKeyPairId,
  ghostMemberId,
  ghostMemberPublicKey,
  adminPublicKey,
  isCreateOrgRequest,
  email,
  userPrivateKeyString,
  authorizeMemberCallback,
  failureCallback,
) {
  // create statisticsKey
  const statisticsKey = await generateAesKey();

  // create member salts and hash
  let memberAuthSalt = "";
  let memberKeySalt = "";
  let authPasscodeHash = "";
  let signedMemberAppPublicKeyWithMetadata = "";
  let encryptedMemberAppPrivateKey = "";
  let encryptedMemberAppSigningPrivateKey = "";
  let memberAppPrivateKey = "";
  let memberAppPublicKey = "";
  let memberAppSigningPrivateKey = "";
  let memberAppSigningPublicKey = "";
  let signedMemberAppPublicKey = "";
  const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);
  const signingCurve = await getSigningCurveFromStorage(sessionId);

  if (!email) {
    memberAuthSalt = generateBCryptSalt();
    memberKeySalt = generateBCryptSalt();
    authPasscodeHash = await derivePbkdf2Encoded(passcode, memberAuthSalt);

    // create memberApp keyPair
    const memberAppKeyPair = await generateEccKeyPair(encryptionCurve);

    // save the memberAppPrivateKey
    memberAppPrivateKey = await exportEccPrivateKey(
      memberAppKeyPair.privateKey,
    );
    memberAppPublicKey = await exportEccPublicKey(memberAppKeyPair.publicKey);
    const memberAppSigningKeyPair =
      await generateEccSigningKeyPair(signingCurve);
    memberAppSigningPrivateKey = await exportEccPrivateKey(
      memberAppSigningKeyPair.privateKey,
    );
    memberAppSigningPublicKey = await exportEccPublicKey(
      memberAppSigningKeyPair.publicKey,
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_PUBLIC_KEY,
      memberAppPublicKey,
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_SIGNING_PUBLIC_KEY,
      memberAppSigningPublicKey,
    );
    // The signing *private* key is deliberately not saved here. It is in itemsToBeEncrypted, so
    // saving it requires the SDK storage key, which is fetched with memberAppId or userId — and
    // on the agentic (passcode, no-email) path neither exists until createMember returns. It is
    // saved after getStorageEncryptionKey below instead.

    // encrypt the memberApp private key with passcode
    encryptedMemberAppPrivateKey = await aeadEncryptWithPasscode(
      memberAppPrivateKey,
      passcode,
      memberKeySalt,
      orgMemberId,
    );

    if (!encryptedMemberAppPrivateKey) {
      console.log("MemberAppPrivateKey encryption failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    // Seal the signing private key with the same passcode-derived key. Sending a
    // raw PEM would let the server forge member-app signatures.
    encryptedMemberAppSigningPrivateKey = await aeadEncryptWithPasscode(
      memberAppSigningPrivateKey,
      passcode,
      memberKeySalt,
      orgMemberId,
    );

    if (!encryptedMemberAppSigningPrivateKey) {
      console.log("MemberAppSigningPrivateKey encryption failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    // sign MemberAppPublicKey (First Layer) with its signing key
    signedMemberAppPublicKey = await signData(
      memberAppPublicKey,
      memberAppSigningPrivateKey,
      await getMemberAppPublicKeyContext(sessionId),
      signingCurve,
      sessionId,
    );

    if (!signedMemberAppPublicKey) {
      console.log("MemberAppPublicKey signature could not be generated.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    signedMemberAppPublicKeyWithMetadata = appendSignatureAndMetadata(
      memberAppPublicKey,
      signedMemberAppPublicKey,
      SigningPublicKeyTags.MEMBER_APP_SIGNING_PUBLIC_KEY_TAG,
      await getMemberAppPublicKeyContext(sessionId),
    );
  }

  // create member keyPair
  const memberKeyPair = await generateEccKeyPair(encryptionCurve);

  // save the memberPrivateKey
  const memberPrivateKey = await exportEccPrivateKey(memberKeyPair.privateKey);
  const memberPublicKey = await exportEccPublicKey(memberKeyPair.publicKey);

  await saveInStorage(
    sessionId,
    SharedPreferences.MEMBER_PUBLIC_KEY,
    memberPublicKey,
  );

  const memberSigningKeyPair = await generateEccSigningKeyPair(signingCurve);
  const memberSigningPrivateKey = await exportEccPrivateKey(
    memberSigningKeyPair.privateKey,
  );
  const memberSigningPublicKey = await exportEccPublicKey(
    memberSigningKeyPair.publicKey,
  );
  await saveInStorage(
    sessionId,
    SharedPreferences.MEMBER_SIGNING_PUBLIC_KEY,
    memberSigningPublicKey,
  );

  if (!email) {
    // sign MemberAppPublicKey(Second Layer) with the member signing key
    const signedMemberAppPublicKeyLayer2 = await signData(
      memberAppPublicKey,
      memberSigningPrivateKey,
      await getMemberAppPublicKeyContextLayer2(
        sessionId,
        signedMemberAppPublicKey,
      ),
      signingCurve,
      sessionId,
    );

    if (!signedMemberAppPublicKeyLayer2) {
      console.log(
        "signedMemberAppPublicKeyLayer2 signature could not be generated.",
      );
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    signedMemberAppPublicKeyWithMetadata = appendSignatureAndMetadata(
      signedMemberAppPublicKeyWithMetadata,
      signedMemberAppPublicKeyLayer2,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
      await getMemberAppPublicKeyContextLayer2(
        sessionId,
        signedMemberAppPublicKey,
      ),
    );
  }
  // NEW IMPLEMENTATION using signDataAndAppendSignatureWithMetadata
  const baseContextForMemberPublicKey = await getMemberPublicKeyContext(
    orgName,
    orgMemberId,
  );

  // First Layer: Sign memberPublicKey using member signing key
  let signedMemberPublickeyWithMetadata =
    await signDataAndAppendSignatureWithMetadata(
      memberPublicKey,
      memberSigningPrivateKey,
      baseContextForMemberPublicKey,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
      signingCurve,
      sessionId,
    );

  if (!signedMemberPublickeyWithMetadata) {
    console.log("MemberPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  // Second Layer: Sign using CreationAppPrivateKey
  const memberCreationAppPrivateKey = await getFromInternalStorage(
    sessionId,
    SharedPreferences.MEMBER_CREATION_APP_SIGNING_PRIVATE_KEY,
  );

  if (!memberCreationAppPrivateKey) {
    throw BayunError.ERROR_MEMBER_CREATION_NOT_ALLOWED;
  }

  signedMemberPublickeyWithMetadata =
    await signDataAndAppendSignatureWithMetadata(
      signedMemberPublickeyWithMetadata,
      memberCreationAppPrivateKey,
      baseContextForMemberPublicKey,
      SigningPublicKeyTags.MEMBER_CREATION_APP_SIGNING_PUBLIC_KEY_TAG,
      signingCurve,
      sessionId,
    );

  if (!signedMemberPublickeyWithMetadata) {
    console.log("MemberPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  // Third Layer (optional): Sign using AuthorizationAppPrivateKey
  const authorizationAppPrivateKey = await getFromInternalStorage(
    sessionId,
    SharedPreferences.AUTHORISATION_APP_SIGNING_PRIVATE_KEY,
  );

  if (authorizationAppPrivateKey) {
    signedMemberPublickeyWithMetadata =
      await signDataAndAppendSignatureWithMetadata(
        signedMemberPublickeyWithMetadata,
        authorizationAppPrivateKey,
        baseContextForMemberPublicKey,
        SigningPublicKeyTags.AUTHORIZATION_APP_SIGNING_PUBLIC_KEY_TAG,
        signingCurve,
        sessionId,
      );

    if (!signedMemberPublickeyWithMetadata) {
      console.log("MemberPublicKey Signature could not be generated.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
  } else {
    // Save half-signed member public key if authorization app private key is not available
    await saveInStorage(
      sessionId,
      SharedPreferences.HALF_SIGNED_MEMBER_PUBLIC_KEY,
      signedMemberPublickeyWithMetadata,
    );
  }

  let encryptedMemberPrivateKey = "";
  let memberPrivateKeyInner_kek = "";
  let userEncryptedMemberPrivateKey = "";
  let memberPrivateKeyOuter_kek = "";

  if (!email) {
    // create memberApp lockBox
    const memberPrivateKeyEncryptionResult = await encryptAsymmetric(
      memberPrivateKey,
      memberAppPublicKey,
      orgMemberId,
      encryptionCurve,
    );
    if (memberPrivateKeyEncryptionResult) {
      encryptedMemberPrivateKey =
        memberPrivateKeyEncryptionResult.encryptedText;
      memberPrivateKeyInner_kek =
        memberPrivateKeyEncryptionResult.keyEncryptionKey;
    }
  } else {
    // create ssoMemberLockBoxRequest
    const ssoMemberLockBoxResult = await encryptAsymmetric(
      memberPrivateKey,
      await getFromStorage(sessionId, SharedPreferences.USER_PUBLIC_KEY),
      await getFromStorage(sessionId, SharedPreferences.USER_ID),
      encryptionCurve,
    );

    if (ssoMemberLockBoxResult) {
      userEncryptedMemberPrivateKey = ssoMemberLockBoxResult.encryptedText;
      memberPrivateKeyOuter_kek = ssoMemberLockBoxResult.keyEncryptionKey;
    }
  }

  // create securityAdmin member lockBox
  const backDoorPublicKey = await getFromStorage(
    sessionId,
    SharedPreferences.BACKDOOR_PUBLIC_KEY,
  );

  let backdoorEncryptedMemberPrivateKey = "";
  let backdoorEncryptedMemberPrivateKey_kek = "";

  if (backDoorPublicKey) {
    const backdoorEncryptedMemberPrivateKeyResult = await encryptAsymmetric(
      memberPrivateKey,
      backDoorPublicKey,
      orgMemberId,
      encryptionCurve,
    );

    if (backdoorEncryptedMemberPrivateKeyResult) {
      backdoorEncryptedMemberPrivateKey =
        backdoorEncryptedMemberPrivateKeyResult.encryptedText;
      backdoorEncryptedMemberPrivateKey_kek =
        backdoorEncryptedMemberPrivateKeyResult.keyEncryptionKey;
    }

    // sign backdoorEncryptedMemberPrivateKey_kek
    const signedBackdoorEncryptedMemberPrivateKey_kek = await signData(
      backdoorEncryptedMemberPrivateKey_kek,
      memberSigningPrivateKey,
      await getMemberPrivateKeyKekContext(sessionId),
      signingCurve,
      sessionId,
    );

    if (!signedBackdoorEncryptedMemberPrivateKey_kek) {
      console.log(
        "signedBackdoorEncryptedMemberPrivateKey_kek Signature could not be generated.",
      );
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    backdoorEncryptedMemberPrivateKey_kek = appendSignatureAndMetadata(
      backdoorEncryptedMemberPrivateKey_kek,
      signedBackdoorEncryptedMemberPrivateKey_kek,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
      await getMemberPrivateKeyKekContext(sessionId),
    );
  }

  // create member lockBox
  const memberKey = await generateAesKey();

  // create lmsEncryptedStatisticsKey
  const lmsStatisticsKey = await getLMSStatisticsKey(
    memberKey,
    orgName,
    orgMemberId,
    appId,
  );
  const lmsEncryptedStatisticsKey = await aeadEncryptWithAssociatedData(
    encodeKey(statisticsKey),
    Constants.LMS_KEY_PREFIX + orgName + orgMemberId + appId,
    lmsStatisticsKey,
  );

  const memberKeyEncryptionResponse = await encryptAsymmetric(
    encodeKey(memberKey),
    memberPublicKey,
    orgMemberId,
    encryptionCurve,
  );

  let encryptedMemberKey = "";
  let encryptedMemberKey_kek = "";

  if (memberKeyEncryptionResponse) {
    encryptedMemberKey = memberKeyEncryptionResponse.encryptedText;
    encryptedMemberKey_kek = memberKeyEncryptionResponse.keyEncryptionKey;
  }

  // encrypt the signing key pair with memberPrivateKey
  const encryptedMemberSigningPrivateKeyResult = await encryptAsymmetric(
    memberSigningPrivateKey,
    memberPublicKey,
    orgMemberId,
    encryptionCurve,
  );

  let encryptedMemberSigningPrivateKey = "";
  let encryptedMemberSigningPrivateKey_kek = "";

  if (encryptedMemberSigningPrivateKeyResult) {
    encryptedMemberSigningPrivateKey =
      encryptedMemberSigningPrivateKeyResult.encryptedText;
    encryptedMemberSigningPrivateKey_kek =
      encryptedMemberSigningPrivateKeyResult.keyEncryptionKey;
  }

  //TODO: the condition is for if encryption type is ECC
  if (true) {
    // sign memberPrivateKeyInner_kek using the member signing key
    const signedMemberPrivateKeyInnerKEK = await signData(
      memberPrivateKeyInner_kek,
      memberSigningPrivateKey,
      await getMemberPrivateKeyInnerKekContext(sessionId),
      signingCurve,
      sessionId,
    );

    if (!signedMemberPrivateKeyInnerKEK) {
      console.log(
        "signedMemberPrivateKeyInnerKEK Signature could not be generated.",
      );
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    memberPrivateKeyInner_kek = appendSignatureAndMetadata(
      memberPrivateKeyInner_kek,
      signedMemberPrivateKeyInnerKEK,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
      await getMemberPrivateKeyInnerKekContext(sessionId),
    );

    // sign memberKeyKEK using Member Private Key and append to memberKey_kek # separated
    const signedMemberKeyKEK = await signData(
      encryptedMemberKey_kek,
      memberSigningPrivateKey,
      await getMemberKeyKekContext(sessionId),
      signingCurve,
      sessionId,
    );

    if (!signedMemberKeyKEK) {
      console.log("signedMemberKeyKEK Signature could not be generated.");
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    encryptedMemberKey_kek = appendSignatureAndMetadata(
      encryptedMemberKey_kek,
      signedMemberKeyKEK,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
      await getMemberKeyKekContext(sessionId),
    );
    // sign memberSigningPrivateKeyKEK using memberPrivateKey
    const signedMemberSigningPrivateKeyKEK = await signData(
      encryptedMemberSigningPrivateKey_kek,
      memberSigningPrivateKey,
      await getMemberSigningPrivateKeyKekContext(sessionId),
      signingCurve,
      sessionId,
    );

    if (!signedMemberSigningPrivateKeyKEK) {
      console.log(
        "signedMemberSigningPrivateKeyKEK Signature could not be generated.",
      );
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    encryptedMemberSigningPrivateKey_kek = appendSignatureAndMetadata(
      encryptedMemberSigningPrivateKey_kek,
      signedMemberSigningPrivateKeyKEK,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
      await getMemberSigningPrivateKeyKekContext(sessionId),
    );

    if (memberPrivateKeyOuter_kek) {
      // sign memberPrivateKeyOuter_kek using userPrivateKey
      // let userPrivateKey = await getFromStorage(
      //   sessionId,
      //   SharedPreferences.USER_PRIVATE_KEY
      // );

      // Read from internal storage, not session storage. createPassphrase saves this key to
      // internal storage for every source but mirrors it into session storage only for
      // BAYUN_ADMIN, so a session-storage read fails closed during non-admin registration.
      // The login path (restoreUserSigningKeys) writes both, so internal storage is the one
      // location populated by every flow that reaches here.
      const userSigningPrivateKey =
        (await getFromInternalStorage(
          sessionId,
          SharedPreferences.USER_SIGNING_PRIVATE_KEY,
        )) ||
        (await getFromStorage(
          sessionId,
          SharedPreferences.USER_SIGNING_PRIVATE_KEY,
        ));
      if (!userSigningPrivateKey) {
        console.log(
          "UserSigningPrivateKey missing; cannot sign memberPrivateKeyOuter_kek.",
        );
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
      const signedMemberPrivateKeyOuterKEK = await signData(
        memberPrivateKeyOuter_kek,
        userSigningPrivateKey,
        getUserPrivateKeyOuterKekContext(email.trim()),
        signingCurve,
        sessionId,
      );

      if (!signedMemberPrivateKeyOuterKEK) {
        console.log(
          "signedMemberPrivateKeyOuterKEK Signature could not be generated.",
        );
        throw BayunError.ERROR_AUTHENTICATION_FAILED;
      }

      memberPrivateKeyOuter_kek = appendSignatureAndMetadata(
        memberPrivateKeyOuter_kek,
        signedMemberPrivateKeyOuterKEK,
        SigningPublicKeyTags.USER_SIGNING_PUBLIC_KEY_TAG,
        getUserPrivateKeyOuterKekContext(email.trim()),
      );
    }
  }

  // sign memberSigningPublicKey (First Layer) using memberSigningPrivateKey
  // and append to memberSigningPublicKey # separated
  const signedMemberSigningPublicKey = await signData(
    memberSigningPublicKey,
    memberSigningPrivateKey,
    await getMemberSigningPublicKeyContext(sessionId),
    signingCurve,
    sessionId,
  );

  if (!signedMemberSigningPublicKey) {
    console.log("MemberSigningPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  let finalSignedMemberSigningPublicKey = appendSignatureAndMetadata(
    memberSigningPublicKey,
    signedMemberSigningPublicKey,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    await getMemberSigningPublicKeyContext(sessionId),
  );

  // sign memberSigningPublicKey (Second Layer) using member signing key and
  // use last signature as context and append to memberSigningPublicKey # separated
  const memberPrivKeySignedMemberSigningPublicKey = await signData(
    memberSigningPublicKey,
    memberSigningPrivateKey,
    await getMemberSigningPublicKeyContextLayer2(
      sessionId,
      signedMemberSigningPublicKey,
    ),
    signingCurve,
    sessionId,
  );

  if (!memberPrivKeySignedMemberSigningPublicKey) {
    console.log("MemberSigningPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  finalSignedMemberSigningPublicKey = appendSignatureAndMetadata(
    finalSignedMemberSigningPublicKey,
    memberPrivKeySignedMemberSigningPublicKey,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    await getMemberSigningPublicKeyContextLayer2(
      sessionId,
      signedMemberSigningPublicKey,
    ),
  );

  let adminEncryptedStatisticsKey = "";
  let adminEncryptedStatisticsKey_kek = "";

  if (adminPublicKey) {
    // encrypt statisticsKey using adminPublicKey
    const adminEncryptedStatisticsKeyResult = await encryptAsymmetric(
      encodeKey(statisticsKey),
      adminPublicKey,
      orgName + orgMemberId + appId,
      encryptionCurve,
    );

    if (adminEncryptedStatisticsKeyResult) {
      adminEncryptedStatisticsKey =
        adminEncryptedStatisticsKeyResult.encryptedText;
      adminEncryptedStatisticsKey_kek =
        adminEncryptedStatisticsKeyResult.keyEncryptionKey;
    }
  }
  // get the appKeyPairIds
  const authorizationAppKeyPairId = await getFromStorage(
    sessionId,
    SharedPreferences.AUTHORISATION_APP_KEY_PAIR_ID,
  );
  const memberCreationAppKeyPairId =
    (await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_CREATION_APP_KEY_PAIR_ID,
    ));

  if (isCreateOrgRequest) {
    await createOrgData(
      sessionId,
      ghostMemberPublicKey,
      ghostMemberId,
      orgName,
      orgMemberId,
      memberPublicKey,
      memberPrivateKey,
      memberSigningPrivateKey,
      memberAuthSalt,
      memberKeySalt,
      authPasscodeHash,
      encryptedMemberPrivateKey,
      memberPrivateKeyInner_kek,
      encryptedMemberAppPrivateKey,
      appId,
      appSecret,
      appKeyPairId,
      encryptedMemberSigningPrivateKey,
      encryptedMemberSigningPrivateKey_kek,
      finalSignedMemberSigningPublicKey,
      statisticsKey,
      lmsStatisticsKey,
      lmsEncryptedStatisticsKey,
      memberKey,
      signedMemberAppPublicKeyWithMetadata,
      signedMemberPublickeyWithMetadata,
      userEncryptedMemberPrivateKey,
      memberPrivateKeyOuter_kek,
      email,
      userPrivateKeyString,
      memberAppSigningPublicKey,
      memberAppSigningPrivateKey,
      encryptedMemberAppSigningPrivateKey,
    );

    // reivew - need this key here, save it just after getting org key
    if (
      !(await getFromInternalStorage(
        sessionId,
        SharedPreferences.AUTHORISATION_APP_SIGNING_PRIVATE_KEY,
      ))
    ) {
      await logoutThenAuthorizeMemberCallback(
        sessionId,
        orgName,
        orgMemberId,
        authorizeMemberCallback,
      );
      return false;
    }

    // if(process.env.SOURCE === Constants.SOURCE.BAYUN_ADMIN) {
    //   return;
    // }

    await getMemberClientData(sessionId, orgMemberId, orgName, passcode, failureCallback);
    return true;
  } else {
    const adminPublicKeyMessage = getMessageFromSignatureString(adminPublicKey);
    const memberPublicKeyContext = await getMemberPublicKeyContext(
      orgName,
      orgMemberId,
    );
    const memberSignedAdminPublicKey = await signData(
      adminPublicKeyMessage,
      memberSigningPrivateKey,
      memberPublicKeyContext,
      signingCurve,
      sessionId,
    );

    if (!memberSignedAdminPublicKey) {
      console.log("memberTrustedAdminPublicKey Signature could not be generated.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    let memberTrustedAdminPublicKey = appendSignatureAndMetadata(
      adminPublicKeyMessage,
      memberSignedAdminPublicKey,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
      memberPublicKeyContext,
    );

    if (!memberTrustedAdminPublicKey) {
      console.log("memberTrustedAdminPublicKey Signature could not be generated.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    // create request object

    const requestBody = {
      adminEncryptedStatisticsKey,
      adminEncryptedStatisticsKey_kek,
      authPasscodeHash,
      authorizationAppKeyPairId,
      baseMemberAppLockBoxRequest: {
        memberPrivateKey: encryptedMemberPrivateKey,
        memberPrivateKeyInner_kek,
      },
      orgMemberId,
      orgName,
      memberCreationAppKeyPairId,
      memberAppKeyPairRequest: {
        privateKey: encryptedMemberAppPrivateKey,
        publicKey: signedMemberAppPublicKeyWithMetadata,
        signingPublicKey: memberAppSigningPublicKey,
        signingPrivateKey: encryptedMemberAppSigningPrivateKey,
      },
      memberAuthSalt,
      memberKeySalt,
      memberTrustedAdminPublicKey,
      memberKeyPairRequest: {
        publicKey: signedMemberPublickeyWithMetadata,
        archivedPublicKey: signedMemberPublickeyWithMetadata,
      },
      memberLockBoxRequest: {
        memberKey: encryptedMemberKey,
        memberKey_kek: encryptedMemberKey_kek,
        orgKey: null,
        orgKey_kek: null,
        memberSigningPrivateKey: encryptedMemberSigningPrivateKey,
        memberSigningPrivateKey_kek: encryptedMemberSigningPrivateKey_kek,
        memberSigningPublicKey: finalSignedMemberSigningPublicKey,
        orgPrivateKey: null,
        orgPrivateKey_kek: null,
        orgPublicKey: null,
      },
      lmsEncryptedStatisticsKey,
      lmsStatisticsKey: encodeKey(lmsStatisticsKey),
      securityAdminMemberLockBoxRequest: {
        memberPrivateKey: backdoorEncryptedMemberPrivateKey,
        memberPrivateKey_kek: backdoorEncryptedMemberPrivateKey_kek,
      },
      ssoMemberLockBoxRequest: {
        memberPrivateKey: userEncryptedMemberPrivateKey,
        memberPrivateKeyOuter_kek,
      },
      email,
    };

    try {
      // create a rest client
      let nonAuthRestClient = null;
      let createMemberResponse = null;

      if (!email) {
        nonAuthRestClient = getNonAuthRestClient(appId, appKeyPairId, sessionId);

        // call the createMember API
        createMemberResponse = await nonAuthRestClient.post(
          "/createMember",
          requestBody,
        );
      } else {
        nonAuthRestClient = getTwoFARestClient(appId, sessionId);

        // call the createMember API
        createMemberResponse = await nonAuthRestClient.post(
          "/twofa/createMember",
          requestBody,
        );
      }

      // console.log(createMemberResponse);
      await logApiResponse("createMemberResponse", createMemberResponse);

      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_APP_ID,
        getMessageFromSignatureString(createMemberResponse.data.memberAppId),
      );

      await saveInStorage(sessionId, SharedPreferences.MEMBER_AUTH_SALT, memberAuthSalt);
      await saveInStorage(sessionId, SharedPreferences.MEMBER_KEY_SALT, memberKeySalt);

      await getRefreshToken(createMemberResponse, nonAuthRestClient);
      await getStorageEncryptionKey(sessionId);

      // Deferred from before the createMember call: this key is encrypted at rest, so it could
      // not be written until the storage key was obtainable (see the note at its generation).
      if (memberAppSigningPrivateKey) {
        await saveInStorage(
          sessionId,
          SharedPreferences.MEMBER_APP_SIGNING_PRIVATE_KEY,
          memberAppSigningPrivateKey,
        );
      }

      await saveInStorage(
        sessionId,
        SharedPreferences.USER_PRIVATE_KEY,
        userPrivateKeyString,
      );
      await saveInStorage(sessionId, SharedPreferences.APP_ID, appId);
      await saveInStorage(sessionId, SharedPreferences.APP_SECRET, appSecret);
      await saveInStorage(sessionId, SharedPreferences.ORG_NAME, orgName);
      await saveInStorage(
        sessionId,
        SharedPreferences.ORG_MEMBER_ID,
        orgMemberId,
      );

      if (
        !(await getFromInternalStorage(
          sessionId,
          SharedPreferences.AUTHORISATION_APP_PRIVATE_KEY,
        ))
      ) {
        await logoutThenAuthorizeMemberCallback(
          sessionId,
          orgName,
          orgMemberId,
          authorizeMemberCallback,
        );
        return false;
      }

      await getMemberClientData(sessionId, orgMemberId, orgName, passcode, failureCallback);

      // marked CBSL
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY,
        memberSigningPrivateKey,
      );

      // marked CBSL
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_KEY,
        encodeKey(memberKey),
      );

      // marked
      await saveInStorage(
        sessionId,
        SharedPreferences.MEMBER_PRIVATE_KEY,
        memberPrivateKey,
      );

      if (memberAppPrivateKey) {
        // marked
        await saveInStorage(
          sessionId,
          SharedPreferences.MEMBER_APP_PRIVATE_KEY,
          memberAppPrivateKey,
        );
      }

      // marked
      await saveInStorage(
        sessionId,
        SharedPreferences.STATISTICS_KEY,
        encodeKey(statisticsKey),
      );
      return true;
    } catch (error) {
      if (error?.bayunAlreadyHandled) {
        throw error;
      }
      console.log(error);
      console.log(error?.response?.data);
      throw error;
    }
  }
}


export async function createMemberAppForSSO(
  sessionId,
  orgName,
  orgMemberId,
  userPrivateKeyString,
  failureCallback,
) {
  // create a rest client
  const nonAuthRestClient = await getSetTwoFARestClient(sessionId);

  const userId = await getFromStorage(sessionId, SharedPreferences.USER_ID);

  try {
    // call the createMemberApp API
    const createMemberAppResponse = await nonAuthRestClient.post(
      "/twofa/createMemberApp",
      {
        orgMemberId,
        orgName,
        userId,
      },
    );

    // console.log(createMemberAppResponse);
    await logApiResponse("createMemberAppResponse", createMemberAppResponse);

    await getRefreshToken(createMemberAppResponse, nonAuthRestClient);

    await getStorageEncryptionKey(sessionId);

    if (userPrivateKeyString) {
      await saveInStorage(
        sessionId,
        SharedPreferences.USER_PRIVATE_KEY,
        userPrivateKeyString,
      );
    }
    await getMemberClientData(
      sessionId,
      orgMemberId,
      orgName,
      //passcode
      null,
      failureCallback,
    );
  } catch (error) {
    if (error?.bayunAlreadyHandled) {
      throw error;
    }
    console.log(error);
    console.log(error?.response?.data);
    throw error;
  }
}

/**
 * This method creates the org and admin data for a new member that will be the first of
 * its org.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} ghostMemberPublicKey member public key of the ghost member.
 * @param {String} ghostMemberId member id of the ghost member.
 * @param {String} orgName name of the org of the member to be created.
 * @param {String} orgMemberId id of the member to be created.
 * @param {String} memberPublicKey memberPublicKey of the member to be created.
 * @param {String} memberPrivateKey memberPrivateKey of the member to be created.
 * @param {String} memberAuthSalt auth salt of the member to be created.
 * @param {String} memberKeySalt keySalt of the member to be created.
 * @param {String} authPasscodeHash hash created with the passcode entered and auth salt.
 * @param {String} encryptedMemberPrivateKey memberPrivateKey encrypted with memberAppPublicKey.
 * @param {String} encryptedMemberPrivateKey_kek kek for the inner encryption of memberPrivateKey.
 * @param {String} encryptedMemberAppPrivateKey memberAppPrivateKey encrypted with passcode.
 * @param {String} encryptedMemberAppSigningPrivateKey memberAppSigningPrivateKey encrypted with passcode.
 * @param {String} memberAppPublicKey memberAppPublicKey of the member to be created.
 * @param {String} appId It is to be provided by the developer for all API calls to LMS.
 * @param {String} appSecret It is to be provided by the developer for all API calls to LMS.
 * @param {String} appKeyPairId It is to be provided by the developer for all API calls to LMS.
 * @param {String} encryptedMemberSigningPrivateKey member signing private key
 * @param {String} encryptedMemberSigningPrivateKey_kek kek for the encrypted memberSigningPrivateKey.
 * @param {String} memberSigningPublicKey member signing public key
 * @param {String} statisticsKey key with which stats are encrypted.
 * @param {String} lmsStatisticsKey lms key to encrypt the statisticsKey.
 * @param {String} lmsEncryptedStatisticsKey statistics key encrypted for LMS.
 */
export async function createOrgData(
  sessionId,
  ghostMemberPubKeyWithSignatures,
  ghostMemberIdWithSignatures,
  orgName,
  orgMemberId,
  memberPublicKey,
  memberPrivateKey,
  memberSigningPrivateKey,
  memberAuthSalt,
  memberKeySalt,
  authPasscodeHash,
  encryptedMemberPrivateKey,
  encryptedMemberPrivateKey_kek,
  encryptedMemberAppPrivateKey,
  appId,
  appSecret,
  appKeyPairId,
  encryptedMemberSigningPrivateKey,
  encryptedMemberSigningPrivateKey_kek,
  memberSigningPublicKey,
  statisticsKey,
  lmsStatisticsKey,
  lmsEncryptedStatisticsKey,
  memberKey,
  signedMemberAppPublicKeyWithMetadata,
  signedMemberPublickeyWithMetadata,
  userEncryptedMemberPrivateKey,
  memberPrivateKeyOuter_kek,
  email,
  userPrivateKeyString,
  memberAppSigningPublicKey = "",
  memberAppSigningPrivateKey = "",
  encryptedMemberAppSigningPrivateKey = "",
) {
  const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);
  const signingCurve = await getSigningCurveFromStorage(sessionId);
  const ghostMemberId = getMessageFromSignatureString(ghostMemberIdWithSignatures);

  // sign ghostMemberId with the first member's signing key
  const memberPrivKeySignedAGhostMemberId = await signData(
    ghostMemberId,
    memberSigningPrivateKey,
    await getGhostMemberIdContext(sessionId),
    signingCurve,
    sessionId,
  );

  if (!memberPrivKeySignedAGhostMemberId) {
    console.log("GhostMemberId Signature could not be generated");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  // appendSignatureAndMetadata returns the whole chain, not just the new layer, so its result
  // replaces the chain. Re-appending it produced `chain#chain#signature$metadata`, duplicating
  // every earlier layer. The LMS reads the server layer at index 1 either way, which is why the
  // corruption went unnoticed, but the duplicated chain was persisted to member.signed_member_id.
  ghostMemberIdWithSignatures = appendSignatureAndMetadata(
    ghostMemberIdWithSignatures,
    memberPrivKeySignedAGhostMemberId,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    await getGhostMemberIdContext(sessionId),
  );

  // sign ghostMemberPublicKey with First Member's Private Key
  const ghostMemberPublicKey = getMessageFromSignatureString(
    ghostMemberPubKeyWithSignatures,
  );

  const memberPrivKeySignedGhostMemberPublicKey = await signData(
    ghostMemberPublicKey,
    memberSigningPrivateKey,
    await getGhostMemberPublicKeyContext(sessionId),
    signingCurve,
    sessionId,
  );

  if (!memberPrivKeySignedGhostMemberPublicKey) {
    console.log("GhostMemberPublicKey Signature could not be generated");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  // Carries tag and context like every other layer. Appending the bare signature left a layer
  // that `split("$").length < 2` guards skip, so it identified neither the signing key nor the
  // context it was produced over and could never be verified.
  ghostMemberPubKeyWithSignatures = appendSignatureAndMetadata(
    ghostMemberPubKeyWithSignatures,
    memberPrivKeySignedGhostMemberPublicKey,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    await getGhostMemberPublicKeyContext(sessionId),
  );

  // create org key and save it
  const orgKey = await generateAesKey();
  // generateAesKey returns raw bytes; the Base64 transport form is what gets encrypted to the
  // member/admin public keys, stored, and signed. Declared here because the signatures cannot be
  // produced until the org signing key pair exists further down.
  const orgKeyEncoded = encodeKey(orgKey);
  let signedOrgKey;
  let signedOrgPrivateKey;

  // encrypt org key with ghostMemberPublicKey
  // const orgKeyGhostEncryptedResult = await encryptAsymmetric(
  //   orgKey,
  //   ghostMemberPublicKey,
  //   ghostMemberId
  // );

  // let encryptedGhostMemberOrgKey = "";
  // let encryptedGhostMemberOrgKey_kek = "";

  // if (orgKeyGhostEncryptedResult) {
  //   encryptedGhostMemberOrgKey =
  //     orgKeyGhostEncryptedResult.encryptedText;
  //   encryptedGhostMemberOrgKey_kek =
  //     orgKeyGhostEncryptedResult.keyEncryptionKey;
  // }

  // encrypt org key with memberPublicKey
  const orgKeyMemberEncryptedResult = await encryptAsymmetric(
    orgKeyEncoded,
    memberPublicKey,
    orgMemberId,
    encryptionCurve,
  );

  let orgKeyMemberEncrypted = "";
  let orgKeyMemberEncrypted_kek = "";

  if (orgKeyMemberEncryptedResult) {
    orgKeyMemberEncrypted = orgKeyMemberEncryptedResult.encryptedText;
    orgKeyMemberEncrypted_kek = orgKeyMemberEncryptedResult.keyEncryptionKey;
  }

  // create admin keypair
  const adminKeyPair = await generateEccKeyPair(encryptionCurve);
  const adminPublicKey = await exportEccPublicKey(adminKeyPair.publicKey);
  const adminPrivateKey = await exportEccPrivateKey(adminKeyPair.privateKey);
  const adminSigningKeyPair = await generateEccSigningKeyPair(signingCurve);
  const adminSigningPublicKey = await exportEccPublicKey(
    adminSigningKeyPair.publicKey,
  );
  const adminSigningPrivateKey = await exportEccPrivateKey(
    adminSigningKeyPair.privateKey,
  );

  // save the admin key pair
  await saveInStorage(
    sessionId,
    SharedPreferences.ADMIN_PUBLIC_KEY,
    adminPublicKey,
  );
  await saveInStorage(
    sessionId,
    SharedPreferences.ADMIN_SIGNING_PUBLIC_KEY,
    adminSigningPublicKey,
  );
  await saveInStorage(
    sessionId,
    SharedPreferences.ADMIN_SIGNING_PRIVATE_KEY,
    adminSigningPrivateKey,
  );

  const memberPublicKeyContext = await getMemberPublicKeyContext(
    orgName,
    orgMemberId,
  );
  const memberSignedAdminPublicKey = await signData(
    adminPublicKey,
    memberSigningPrivateKey,
    memberPublicKeyContext,
    signingCurve,
    sessionId,
  );

  if (!memberSignedAdminPublicKey) {
    console.log("memberTrustedAdminPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  let memberTrustedAdminPublicKey = appendSignatureAndMetadata(
    adminPublicKey,
    memberSignedAdminPublicKey,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    memberPublicKeyContext,
  );

  if (!memberTrustedAdminPublicKey) {
    console.log("memberTrustedAdminPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  const adminPublicKeyContext = await getAdminPublicKeyContext(sessionId);
  const adminSignedAdminPublicKey = await signData(
    adminPublicKey,
    adminSigningPrivateKey,
    adminPublicKeyContext,
    signingCurve,
    sessionId,
  );

  if (!adminSignedAdminPublicKey) {
    console.log("memberTrustedAdminPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  memberTrustedAdminPublicKey = appendSignatureAndMetadata(
    memberTrustedAdminPublicKey,
    adminSignedAdminPublicKey,
    SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
    adminPublicKeyContext,
  );

  if (!memberTrustedAdminPublicKey) {
    console.log("memberTrustedAdminPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  // sign adminPublicKey (First Layer) with admin signing key
  const signedAdminPublicKey = await signData(
    adminPublicKey,
    adminSigningPrivateKey,
    await getAdminPublicKeyContext(sessionId),
    signingCurve,
    sessionId,
  );

  if (!signedAdminPublicKey) {
    console.log("AdminPublicKey Signature could not be generated");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  let signedAdminPublicKeyWithMetadata = appendSignatureAndMetadata(
    adminPublicKey,
    signedAdminPublicKey,
    SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
    await getAdminPublicKeyContext(sessionId),
  );

  const orgAppApprovalContext = await getOrgAppApprovalContext(orgName, appId);
  // Admin SIGNING key: AdminPublicKeyTag resolves to the admin signing public key, so a
  // signature made with the admin encryption key could never verify.
  const signedAppIdSignature = await signData(
    appId,
    adminSigningPrivateKey,
    orgAppApprovalContext,
    signingCurve,
    sessionId,
  );
  const signedAppId = appendSignatureAndMetadata(
    appId,
    signedAppIdSignature,
    SigningPublicKeyTags.ADMIN_PUBLIC_KEY_TAG,
    orgAppApprovalContext,
  );

  // sign adminPublicKey (Second Layer) with first member's memberPrivateKey
  const signatureContext = `${await getAdminPublicKeyContext(
    sessionId,
  )}$${signedAdminPublicKey}`;

  const memberPrivKeySignedAdminPublicKey = await signData(
    adminPublicKey,
    memberSigningPrivateKey,
    signatureContext,
    signingCurve,
    sessionId,
  );

  if (!memberPrivKeySignedAdminPublicKey) {
    console.log("AdminPublicKey Signature could not be generated");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  signedAdminPublicKeyWithMetadata = appendSignatureAndMetadata(
    signedAdminPublicKeyWithMetadata,
    memberPrivKeySignedAdminPublicKey,
    SigningPublicKeyTags.FIRST_MEMBER_PUBLIC_KEY_TAG,
    signatureContext,
  );

  // encrypt orgKey with adminPublicKey
  const orgKeyAdminEncryptedResult = await encryptAsymmetric(
    orgKeyEncoded,
    adminPublicKey,
    orgMemberId,
    encryptionCurve,
  );

  let orgKeyAdminEncrypted = "";
  let orgKeyAdminEncrypted_kek = "";

  if (orgKeyAdminEncryptedResult) {
    orgKeyAdminEncrypted = orgKeyAdminEncryptedResult.encryptedText;
    orgKeyAdminEncrypted_kek = orgKeyAdminEncryptedResult.keyEncryptionKey;
  }

  // create backdoor key pair
  const backdoorKeyPair = await generateEccKeyPair(encryptionCurve);
  const backdoorPublickey = await exportEccPublicKey(backdoorKeyPair.publicKey);
  const backdoorPrivateKey = await exportEccPrivateKey(
    backdoorKeyPair.privateKey,
  );
  const backdoorSigningKeyPair =
    await generateEccSigningKeyPair(signingCurve);
  const backdoorSigningPublicKey = await exportEccPublicKey(
    backdoorSigningKeyPair.publicKey,
  );
  const backdoorSigningPrivateKey = await exportEccPrivateKey(
    backdoorSigningKeyPair.privateKey,
  );

  //saving backdoorPublicKey in local storage
  if (backdoorPublickey) {
    await saveInStorage(
      sessionId,
      SharedPreferences.BACKDOOR_PUBLIC_KEY,
      backdoorPublickey,
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.BACKDOOR_SIGNING_PUBLIC_KEY,
      backdoorSigningPublicKey,
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.BACKDOOR_SIGNING_PRIVATE_KEY,
      backdoorSigningPrivateKey,
    );
  }

  // encrypt memberKey with memberPublicKey
  const memberKeyMemberEncryptedResult = await encryptAsymmetric(
    encodeKey(memberKey),
    memberPublicKey,
    orgMemberId,
    encryptionCurve,
  );

  let memberKeyMemberEncrypted = "";
  let memberKeyMemberEncrypted_kek = "";

  if (memberKeyMemberEncryptedResult) {
    memberKeyMemberEncrypted = memberKeyMemberEncryptedResult.encryptedText;
    memberKeyMemberEncrypted_kek =
      memberKeyMemberEncryptedResult.keyEncryptionKey;
  }

  // encrypt memberPrivateKey with backdoorPublicKey
  const memberPrivateKeyBackdoorEncryptedResult = await encryptAsymmetric(
    memberPrivateKey,
    backdoorPublickey,
    orgMemberId,
    encryptionCurve,
  );

  let memberPrivateKeyBackdoorEncrypted = "";
  let memberPrivateKeyBackdoorEncrypted_kek = "";

  if (memberPrivateKeyBackdoorEncryptedResult) {
    memberPrivateKeyBackdoorEncrypted =
      memberPrivateKeyBackdoorEncryptedResult.encryptedText;
    memberPrivateKeyBackdoorEncrypted_kek =
      memberPrivateKeyBackdoorEncryptedResult.keyEncryptionKey;
  }

  // sign backdoorEncryptedMemberPrivateKey_kek
  const signedMemberPrivateKeyBackdoorEncrypted_kek = await signData(
    memberPrivateKeyBackdoorEncrypted_kek,
    memberSigningPrivateKey,
    await getMemberPrivateKeyKekContext(sessionId),
    signingCurve,
    sessionId,
  );

  if (!signedMemberPrivateKeyBackdoorEncrypted_kek) {
    console.log(
      "signedMemberPrivateKeyBackdoorEncrypted_kek Signature could not be generated.",
    );
    throw BayunError.ERROR_AUTHENTICATION_FAILED;
  }

  memberPrivateKeyBackdoorEncrypted_kek = appendSignatureAndMetadata(
    memberPrivateKeyBackdoorEncrypted_kek,
    signedMemberPrivateKeyBackdoorEncrypted_kek,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    await getMemberPrivateKeyKekContext(sessionId),
  );

  // encrypt adminPrivateKey with memberPublicKey
  const adminPrivateKeyMemberEncryptedResult = await encryptAsymmetric(
    adminPrivateKey,
    memberPublicKey,
    orgMemberId,
    encryptionCurve,
  );

  let adminPrivateKeyMemberEncrypted = "";
  let adminPrivateKeyMemberEncrypted_kek = "";

  if (adminPrivateKeyMemberEncryptedResult) {
    adminPrivateKeyMemberEncrypted =
      adminPrivateKeyMemberEncryptedResult.encryptedText;
    adminPrivateKeyMemberEncrypted_kek =
      adminPrivateKeyMemberEncryptedResult.keyEncryptionKey;
  }

  // encrypt backdoorPrivateKey with memberPublicKey
  const backdoorPrivateKeyMemberEncryptedResult = await encryptAsymmetric(
    backdoorPrivateKey,
    memberPublicKey,
    orgMemberId,
    encryptionCurve,
  );

  let backdoorPrivateKeyMemberEncrypted = "";
  let backdoorPrivateKeyMemberEncrypted_kek = "";

  if (backdoorPrivateKeyMemberEncryptedResult) {
    backdoorPrivateKeyMemberEncrypted =
      backdoorPrivateKeyMemberEncryptedResult.encryptedText;
    backdoorPrivateKeyMemberEncrypted_kek =
      backdoorPrivateKeyMemberEncryptedResult.keyEncryptionKey;
  }

  // Escrow the admin and backdoor SIGNING private keys the same way as their encryption
  // counterparts. They are generated once here; without escrow they vanish with this session and
  // every later admin-signed operation fails permanently.
  const adminSigningPrivateKeyMemberEncryptedResult = await encryptAsymmetric(
    adminSigningPrivateKey,
    memberPublicKey,
    orgMemberId,
    encryptionCurve,
  );
  if (!adminSigningPrivateKeyMemberEncryptedResult) {
    console.log("AdminSigningPrivateKey encryption failed.");
    throw BayunError.ERROR_AUTHENTICATION_FAILED;
  }
  const adminSigningPrivateKeyMemberEncrypted =
    adminSigningPrivateKeyMemberEncryptedResult.encryptedText;
  let adminSigningPrivateKeyMemberEncrypted_kek =
    adminSigningPrivateKeyMemberEncryptedResult.keyEncryptionKey;

  const backdoorSigningPrivateKeyMemberEncryptedResult = await encryptAsymmetric(
    backdoorSigningPrivateKey,
    memberPublicKey,
    orgMemberId,
    encryptionCurve,
  );
  if (!backdoorSigningPrivateKeyMemberEncryptedResult) {
    console.log("BackdoorSigningPrivateKey encryption failed.");
    throw BayunError.ERROR_AUTHENTICATION_FAILED;
  }
  const backdoorSigningPrivateKeyMemberEncrypted =
    backdoorSigningPrivateKeyMemberEncryptedResult.encryptedText;
  let backdoorSigningPrivateKeyMemberEncrypted_kek =
    backdoorSigningPrivateKeyMemberEncryptedResult.keyEncryptionKey;

  // sign backdoorPublicKey (first layer) with backdoor signing key
  const signedBackdoorPublicKey = await signData(
    backdoorPublickey,
    backdoorSigningPrivateKey,
    await getBackdoorPublicKeyContext(sessionId),
    signingCurve,
    sessionId,
  );

  if (!signedBackdoorPublicKey) {
    console.log("BackdoorPublicKey Signature could not be generated");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  let signedBackdoorPublicKeyWithMetadata = appendSignatureAndMetadata(
    backdoorPublickey,
    signedBackdoorPublicKey,
    SigningPublicKeyTags.BACKDOOR_SIGNING_PUBLIC_KEY_TAG,
    await getBackdoorPublicKeyContext(sessionId),
  );

  if (!signedBackdoorPublicKeyWithMetadata) {
    console.log("BackdoorPublicKey Signature could not be generated");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  // sign backdoorPublicKey (second layer) with admin signing key
  const signedBackdoorPublicKeyAdminLayer = await signData(
    backdoorPublickey,
    adminSigningPrivateKey,
    await getBackdoorPublicKeyContextLayer2(sessionId, signedBackdoorPublicKey),
    signingCurve,
    sessionId,
  );

  if (!signedBackdoorPublicKeyAdminLayer) {
    console.log("BackdoorPublicKey Admin Signature could not be generated");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  signedBackdoorPublicKeyWithMetadata = appendSignatureAndMetadata(
    signedBackdoorPublicKeyWithMetadata,
    signedBackdoorPublicKeyAdminLayer,
    SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
    await getBackdoorPublicKeyContextLayer2(sessionId, signedBackdoorPublicKey),
  );

  // sign backdoorPublicKey (third layer) using first member's signing key
  const signedBackdoorPublicKeyLayer2 = await signData(
    backdoorPublickey,
    memberSigningPrivateKey,
    await getBackdoorPublicKeyContextLayer2(
      sessionId,
      signedBackdoorPublicKeyAdminLayer,
    ),
    signingCurve,
    sessionId,
  );

  if (!signedBackdoorPublicKeyLayer2) {
    console.log("BackdoorPublicKey Member Signature could not be generated");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  signedBackdoorPublicKeyWithMetadata = appendSignatureAndMetadata(
    signedBackdoorPublicKeyWithMetadata,
    signedBackdoorPublicKeyLayer2,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    await getBackdoorPublicKeyContextLayer2(
      sessionId,
      signedBackdoorPublicKeyAdminLayer,
    ),
  );

  // create a signing key pair for the org
  const orgSigningKeyPair = await generateEccSigningKeyPair(signingCurve);
  const orgSigningPrivateKey = await exportEccPrivateKey(
    orgSigningKeyPair.privateKey,
  );
  const orgSigningPublicKey = await exportEccPublicKey(
    orgSigningKeyPair.publicKey,
  );

  // save in local storage
  await saveInStorage(
    sessionId,
    SharedPreferences.ORG_SIGNING_PUBLIC_KEY,
    orgSigningPublicKey,
  );

  signedOrgKey = await signData(
    orgKeyEncoded,
    memberSigningPrivateKey,
    await getOrgKeyContext(sessionId),
    signingCurve,
    sessionId,
  );
  signedOrgPrivateKey = await signData(
    orgSigningPrivateKey,
    memberSigningPrivateKey,
    await getOrgPrivateKeyContext(sessionId),
    signingCurve,
    sessionId,
  );
  if (!signedOrgKey || !signedOrgPrivateKey) {
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  // encrypt the signing key pair with memberPrivateKey
  const encryptedOrgSigningPrivateKeyResult = await encryptAsymmetric(
    orgSigningPrivateKey,
    memberPublicKey,
    orgMemberId,
    encryptionCurve,
  );

  let encryptedOrgSigningPrivateKey = "";
  let encryptedOrgSigningPrivateKey_kek = "";

  if (encryptedOrgSigningPrivateKeyResult) {
    encryptedOrgSigningPrivateKey =
      encryptedOrgSigningPrivateKeyResult.encryptedText;
    encryptedOrgSigningPrivateKey_kek =
      encryptedOrgSigningPrivateKeyResult.keyEncryptionKey;
  }

  // sign orgPublicKey (First Layer) using orgPrivateKey  and append to
  // orgPublicKey # separated
  const signedOrgPublicKey = await signData(
    orgSigningPublicKey,
    orgSigningPrivateKey,
    await getOrgPublicKeyContext(sessionId),
    signingCurve,
    sessionId,
  );

  if (!signedOrgPublicKey) {
    console.log("OrgPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  let finalOrgSigningPublicKey = appendSignatureAndMetadata(
    orgSigningPublicKey,
    signedOrgPublicKey,
    SigningPublicKeyTags.ORG_PUBLIC_KEY_TAG,
    await getOrgPublicKeyContext(sessionId),
  );

  // sign OrgPublicKey (Second Layer) using member signing key
  // and use last signature as context and append to OrgPublicKey # separated
  const memberPrivKeySignedOrgPublicKey = await signData(
    orgSigningPublicKey,
    memberSigningPrivateKey,
    await getOrgPublicKeyContextLayer2(sessionId, signedOrgPublicKey),
    signingCurve,
    sessionId,
  );

  if (!memberPrivKeySignedOrgPublicKey) {
    console.log("OrgPublicKey Signature could not be generated.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  finalOrgSigningPublicKey = appendSignatureAndMetadata(
    finalOrgSigningPublicKey,
    memberPrivKeySignedOrgPublicKey,
    SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
    await getOrgPublicKeyContextLayer2(sessionId, signedOrgPublicKey),
  );

  // encrypt statisticsKey using adminPublicKey
  const adminEncryptedStatisticsKeyResult = await encryptAsymmetric(
    encodeKey(statisticsKey),
    adminPublicKey,
    orgName + orgMemberId + appId,
    encryptionCurve,
  );

  let adminEncryptedStatisticsKey = "";
  let adminEncryptedStatisticsKey_kek = "";

  if (adminEncryptedStatisticsKeyResult) {
    adminEncryptedStatisticsKey =
      adminEncryptedStatisticsKeyResult.encryptedText;
    adminEncryptedStatisticsKey_kek =
      adminEncryptedStatisticsKeyResult.keyEncryptionKey;
  }

  //TODO: remove this true into check for encryption type later. Signing is to be done with ECC only.
  if (true) {
    // sign orgKeyAdminEncrypted_kek using adminPrivateKey and append to orgKey_kek # separated
    const signedOrgKeyKEK = await signData(
      orgKeyAdminEncrypted_kek,
      adminSigningPrivateKey,
      await getOrgKeyKekAdminOrgContext(sessionId),
      signingCurve,
      sessionId,
    );

    if (!signedOrgKeyKEK) {
      console.log("OrgKeyKEK Signature could not be generated.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    orgKeyAdminEncrypted_kek = appendSignatureAndMetadata(
      orgKeyAdminEncrypted_kek,
      signedOrgKeyKEK,
      SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
      await getOrgKeyKekAdminOrgContext(sessionId),
    );

    // sign orgKeyMemberEncrypted_kek using Admin Private Key and append to orgKey_kek # separated
    const signedMemberEncryptedOrgKeyKEK = await signData(
      orgKeyMemberEncrypted_kek,
      adminSigningPrivateKey,
      await getOrgKeyKekMemberLockboxContext(sessionId),
      signingCurve,
      sessionId,
    );

    if (!signedMemberEncryptedOrgKeyKEK) {
      console.log(
        "orgKeyMemberEncrypted_kek Signature could not be generated.",
      );
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    orgKeyMemberEncrypted_kek = appendSignatureAndMetadata(
      orgKeyMemberEncrypted_kek,
      signedMemberEncryptedOrgKeyKEK,
      SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
      await getOrgKeyKekMemberLockboxContext(sessionId),
    );

    // sign orgPrivateKey_kek using adminPrivateKey
    const signedOrgPrivateKeyKEK = await signData(
      encryptedOrgSigningPrivateKey_kek,
      adminSigningPrivateKey,
      await getOrgPrivateKeyKekContext(sessionId),
      signingCurve,
      sessionId,
    );

    if (!signedOrgPrivateKeyKEK) {
      console.log(
        "encryptedOrgSigningPrivateKey_kek Signature could not be generated.",
      );
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    encryptedOrgSigningPrivateKey_kek = appendSignatureAndMetadata(
      encryptedOrgSigningPrivateKey_kek,
      signedOrgPrivateKeyKEK,
      SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
      await getOrgPrivateKeyKekContext(sessionId),
    );

    // sign memberKeyKEK using Member Private Key and append to memberKey_kek # separated
    const signedMemberKeyKEK = await signData(
      memberKeyMemberEncrypted_kek,
      memberSigningPrivateKey,
      await getMemberKeyKekContext(sessionId),
      signingCurve,
      sessionId,
    );

    if (!signedMemberKeyKEK) {
      console.log(
        "memberKeyMemberEncrypted_kek Signature could not be generated.",
      );
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    memberKeyMemberEncrypted_kek = appendSignatureAndMetadata(
      memberKeyMemberEncrypted_kek,
      signedMemberKeyKEK,
      SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
      await getMemberKeyKekContext(sessionId),
    );

    // sign backdoorPrivateKeyKEK using adminPrivateKey
    const signedBackdoorPrivateKey_kek = await signData(
      backdoorPrivateKeyMemberEncrypted_kek,
      adminSigningPrivateKey,
      await getBackdoorPrivateKeyPartKekContext(orgName, orgMemberId),
      signingCurve,
      sessionId,
    );

    if (!signedBackdoorPrivateKey_kek) {
      console.log(
        "backdoorPrivateKeyMemberEncrypted_kek Signature could not be generated.",
      );
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    backdoorPrivateKeyMemberEncrypted_kek = appendSignatureAndMetadata(
      backdoorPrivateKeyMemberEncrypted_kek,
      signedBackdoorPrivateKey_kek,
      SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
      await getBackdoorPrivateKeyPartKekContext(orgName, orgMemberId),
    );

    // sign adminPrivateKey_kek using Admin Private Key and append to adminPrivateKey_kek # separated
    const signedAdminPrivateKeyKEK = await signData(
      adminPrivateKeyMemberEncrypted_kek,
      adminSigningPrivateKey,
      await getAdminPrivateKeyKekContext(orgName, orgMemberId),
      signingCurve,
      sessionId,
    );

    if (!signedAdminPrivateKeyKEK) {
      console.log(
        "adminPrivateKeyMemberEncrypted_kek Signature could not be generated.",
      );
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    adminPrivateKeyMemberEncrypted_kek = appendSignatureAndMetadata(
      adminPrivateKeyMemberEncrypted_kek,
      signedAdminPrivateKeyKEK,
      SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
      await getAdminPrivateKeyKekContext(orgName, orgMemberId),
    );

    // The escrowed *signing* keys need the same attestation as the encryption key above.
    // Org creation signed only the encryption KEK, so a client re-fetching admin sensitive data
    // had no way to tell a genuine signing key from one the server substituted - and the signing
    // key is the anchor for every AdminSigningPublicKeyTag verification in the org. The promote
    // path already signs its KEK; this closes the same gap on the org-creation path.
    for (const escrow of [
      {
        get: () => adminSigningPrivateKeyMemberEncrypted_kek,
        set: (value) => {
          adminSigningPrivateKeyMemberEncrypted_kek = value;
        },
        context: await getAdminSigningPrivateKeyKekContext(orgName, orgMemberId),
        label: "adminSigningPrivateKeyMemberEncrypted_kek",
      },
      {
        get: () => backdoorSigningPrivateKeyMemberEncrypted_kek,
        set: (value) => {
          backdoorSigningPrivateKeyMemberEncrypted_kek = value;
        },
        context: await getBackdoorSigningPrivateKeyKekContext(orgName, orgMemberId),
        label: "backdoorSigningPrivateKeyMemberEncrypted_kek",
      },
    ]) {
      const signature = await signData(
        escrow.get(),
        adminSigningPrivateKey,
        escrow.context,
        signingCurve,
        sessionId,
      );
      if (!signature) {
        console.log(`${escrow.label} Signature could not be generated.`);
        throw BayunError.ERROR_AUTHENTICATION_FAILED;
      }
      escrow.set(
        appendSignatureAndMetadata(
          escrow.get(),
          signature,
          SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
          escrow.context,
        ),
      );
    }
  }

  // get the app keyPair ids
  const authorizationAppKeyPairId = await getFromStorage(
    sessionId,
    SharedPreferences.AUTHORISATION_APP_KEY_PAIR_ID,
  );
  const orgCreationAppKeyPairId =
    (await getFromStorage(
      sessionId,
      SharedPreferences.ORG_CREATION_APP_KEY_PAIR_ID,
    ));

  // The /createFirstMemberAndOrg API requires an OrgCreation app key pair.
  // If the application isn't configured with the OrgCreation role, this id
  // is null/empty and the server rejects the request with an INVALID_VALUE
  // error ("orgCreationAppKeyPairId is required and cannot be null or empty").
  // Fail fast here with a meaningful BayunError instead of firing a request
  // that is guaranteed to fail.
  if (!orgCreationAppKeyPairId) {
    console.log(
      "orgCreationAppKeyPairId is missing. The application does not have the OrgCreation role and cannot create an org.",
    );
    throw BayunError.APP_DOES_NOT_HAVE_THE_CORRECT_ROLE;
  }

  const memberCreationAppKeyPairId =
    (await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_CREATION_APP_KEY_PAIR_ID,
    ));
  // create request object
  const requestBody = {
    adminOrgLockBoxRequest: {
      orgKey: orgKeyAdminEncrypted,
      orgKey_kek: orgKeyAdminEncrypted_kek,
    },
    adminEncryptedStatisticsKey,
    adminEncryptedStatisticsKey_kek,
    adminKeyPairRequest: {
      publicKey: signedAdminPublicKeyWithMetadata,
      signingPublicKey: adminSigningPublicKey,
    },
    authPasscodeHash,
    authorizationAppKeyPairId,
    backdoorKeyPairRequest: {
      publicKey: signedBackdoorPublicKeyWithMetadata,
      signingPublicKey: backdoorSigningPublicKey,
    },
    baseMemberAppLockBoxRequest: {
      memberPrivateKey: encryptedMemberPrivateKey,
      memberPrivateKeyInner_kek: encryptedMemberPrivateKey_kek,
    },
    orgMemberId,
    orgKeyDetailRequest: {
      signedOrgKey,
      signedOrgPrivateKey,
    },
    orgName,
    createOrgSettingsRequest: await getOrgSettingsRequest(
      orgKey,
      orgName,
      adminSigningPrivateKey,
      sessionId,
    ),
    orgCreationAppKeyPairId,
    memberCreationAppKeyPairId,
    email: email,
    memberAppKeyPairRequest: {
      privateKey: encryptedMemberAppPrivateKey,
      publicKey: signedMemberAppPublicKeyWithMetadata,
      signingPublicKey: memberAppSigningPublicKey,
      signingPrivateKey: encryptedMemberAppSigningPrivateKey,
    },
    memberAuthSalt,
    memberKeyPairRequest: {
      publicKey: signedMemberPublickeyWithMetadata,
      archivedPublicKey: signedMemberPublickeyWithMetadata,
    },
    memberKeySalt,
    memberTrustedAdminPublicKey,
    memberLockBoxRequest: {
      adminPrivateKey: adminPrivateKeyMemberEncrypted,
      adminPrivateKey_kek: adminPrivateKeyMemberEncrypted_kek,
      adminSigningPrivateKey: adminSigningPrivateKeyMemberEncrypted,
      adminSigningPrivateKey_kek: adminSigningPrivateKeyMemberEncrypted_kek,
      backdoorPrivateKey: backdoorPrivateKeyMemberEncrypted,
      backdoorPrivateKey_kek: backdoorPrivateKeyMemberEncrypted_kek,
      backdoorSigningPrivateKey: backdoorSigningPrivateKeyMemberEncrypted,
      backdoorSigningPrivateKey_kek: backdoorSigningPrivateKeyMemberEncrypted_kek,
      orgKey: orgKeyMemberEncrypted,
      orgKey_kek: orgKeyMemberEncrypted_kek,
      memberKey: memberKeyMemberEncrypted,
      memberKey_kek: memberKeyMemberEncrypted_kek,
      orgPrivateKey: encryptedOrgSigningPrivateKey,
      orgPrivateKey_kek: encryptedOrgSigningPrivateKey_kek,
      orgPublicKey: finalOrgSigningPublicKey,
      memberSigningPrivateKey: encryptedMemberSigningPrivateKey,
      memberSigningPrivateKey_kek: encryptedMemberSigningPrivateKey_kek,
      memberSigningPublicKey: memberSigningPublicKey,
    },
    ghostMemberId: ghostMemberIdWithSignatures,
    ghostMemberPublicKey: ghostMemberPubKeyWithSignatures,
    signedAppId,
    lmsEncryptedStatisticsKey,
    lmsStatisticsKey: encodeKey(lmsStatisticsKey),
    securityAdminMemberLockBoxRequest: {
      memberPrivateKey: memberPrivateKeyBackdoorEncrypted,
      memberPrivateKey_kek: memberPrivateKeyBackdoorEncrypted_kek,
    },
    ssoMemberLockBoxRequest: {
      memberPrivateKey: userEncryptedMemberPrivateKey,
      memberPrivateKeyOuter_kek,
    },
  };

  try {
    // create a rest client
    let nonAuthRestClient = null;
    let createOrgResponse = null;

    if (!email) {
      nonAuthRestClient = getNonAuthRestClient(appId, appKeyPairId, sessionId);
      // call the createOrg API
      createOrgResponse = await nonAuthRestClient.post(
        "/createFirstMemberAndOrg",
        requestBody,
      );
    } else {
      nonAuthRestClient = getTwoFARestClient(appId, sessionId);

      // call the createOrg API
      createOrgResponse = await nonAuthRestClient.post(
        "/twofa/createFirstMemberAndOrg",
        requestBody,
      );
    }

    // console.log(createOrgResponse);
    // needs to be removed
    await logApiResponse("createOrgResponse", createOrgResponse);

    // add the admin private key signature to member public key which we got from createOrgResponse, call api and save it
    // Peel the last signature (ghostMemberSignature) only when the authorization
    // app key is present. If authorizationAppKeyPairId is missing, the
    // authorization private key is not available — leave the chain intact.
    const memberPublicKey = createOrgResponse.data.memberPublicKey;
    let memberPublicKeyForSigning = memberPublicKey;
    if (authorizationAppKeyPairId) {
      const memberPublicKeyParts = memberPublicKey.split("#");
      memberPublicKeyForSigning = memberPublicKeyParts.slice(0, -1).join("#");
    }
    let signedMemberPublickeyWithMetadata =
      await signDataAndAppendSignatureWithMetadata(
        memberPublicKeyForSigning,
        adminSigningPrivateKey,
        await getMemberPublicKeyContext(orgName, orgMemberId),
        SigningPublicKeyTags.ADMIN_SIGNING_PUBLIC_KEY_TAG,
        signingCurve,
        sessionId,
      );

    // Add signature to member public key using backdoor private key
    signedMemberPublickeyWithMetadata =
      await signDataAndAppendSignatureWithMetadata(
        signedMemberPublickeyWithMetadata,
        backdoorSigningPrivateKey,
        await getMemberPublicKeyContext(orgName, orgMemberId),
        SigningPublicKeyTags.BACKDOOR_SIGNING_PUBLIC_KEY_TAG,
        signingCurve,
        sessionId,
      );

    // call api /updateSignedMemberPublicKey with the signedMemberPublickeyWithMetadata
    const updateSignedMemberPublicKeyResponse = await nonAuthRestClient.post(
      "/updateSignedMemberPublicKey",
      {
        publicKey: signedMemberPublickeyWithMetadata,
        archivedPublicKey: signedMemberPublickeyWithMetadata,
      },
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_ID,
      getMessageFromSignatureString(createOrgResponse.data.memberAppId),
    );
    // marked save all the sensitive info with storagekey
    // save the auth token and other data
    // review - to be removed
    // await saveInStorage(
    //   sessionId,
    //   SharedPreferences.AUTH_TOKEN,
    //   createOrgResponse.data.authToken
    // );

    await getRefreshToken(createOrgResponse, nonAuthRestClient);

    await getStorageEncryptionKey(sessionId);
    await saveInStorage(
      sessionId,
      SharedPreferences.USER_PRIVATE_KEY,
      userPrivateKeyString,
    );
    await saveInStorage(sessionId, SharedPreferences.APP_ID, appId);
    // review - first point
    await saveInStorage(sessionId, SharedPreferences.APP_SECRET, appSecret);
    await saveInStorage(sessionId, SharedPreferences.ORG_NAME, orgName);
    await saveInStorage(
      sessionId,
      SharedPreferences.ORG_MEMBER_ID,
      orgMemberId,
    );

    // these field would be kept in encrypted form
    // marked CBSL
    await saveInStorage(
      sessionId,
      SharedPreferences.ORG_SIGNING_PRIVATE_KEY,
      orgSigningPrivateKey,
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.ORG_KEY,
      orgKeyEncoded,
    );
  } catch (error) {
    console.log(error);
    console.log(error?.response?.data ?? error);
    throw getErrorMessage(sessionId, error?.response?.data ?? error);
  }
}

export const authorizeMemberAPI = async (
  appId,
  appSecret,
  // memberPublicKeyMetadataString,
  orgName,
  orgMemberId,
  bayunServerPublicKey,
  successCallback,
  failureCallback,
) => {
  // const memberPublicKeyMetadata = deserializeThrift(
  //   memberPublicKeyMetadataString,
  //   new MemberPublicKeyMetadata(),
  // );

  // // messageWithSignatures's format is message#Signature$metadata#signature$metadata
  // const messageAndSignatures =
  //   memberPublicKeyMetadata.signedPublicKey.split("#");
  // const memberPublicKey = messageAndSignatures[0];
  // const lastSignature = messageAndSignatures[messageAndSignatures.length - 1];

  // //signature's format is signature+$+metadata
  // const signatureAndMetadata = lastSignature.split("$");

  // if (signatureAndMetadata.length < 2) {
  //   throw BayunError.ERROR_SOMETHING_WENT_WRONG;
  // }

  // const creationAppPrivKeySignedMemberPublicKey = signatureAndMetadata[0];

  // // sign memberPublicKey (Third Layer) using AuthorizationAppPrivateKey
  // const signatureContext = `memberPublicKey$${memberPublicKeyMetadata.orgName}${memberPublicKeyMetadata.orgMemberId}$${creationAppPrivKeySignedMemberPublicKey}`;

  let appKeyPairId = "";
  const appPrivateKey = await getFromInternalStorage(
    sessionId,
    SharedPreferences.AUTHORISATION_APP_SIGNING_PRIVATE_KEY,
  );

  // create a rest client (no raw app secret on the wire)
  // sessionId so the account key returned by /applicationKeyPairs is recorded.
  const restClient = getTwoFARestClient(appId, sessionId);

  const serverVerificationSalt = generateBCryptSalt();
  console.log(appSecret, appId);
  const appSecretHash = await generateHMacHash(appSecret, appId);

  try {
    // call getApplicationKeyPair API
    const getApplicationKeyPairResponse = await restClient.post(
      "/applicationKeyPairs",
      {
        bayunServerPublicKey,
        requestSource: Constants.SOURCE.BASE_KIT,
        serverVerificationSalt,
        appSecretHash,
      },
    );
    //TODO code to verify the server salt using serverPublicKey
    // console.log(getApplicationKeyPairResponse);
    await logApiResponse(
      "getApplicationKeyPairResponse",
      getApplicationKeyPairResponse,
    );

    // create appPrivKeyDecryptionKey

    const appPrivKeyDecryptionKey = await derivePbkdf2Raw(
      appSecret,
      getApplicationKeyPairResponse.data.appSecretSalt,
    );

    for (const appKeyPair of getApplicationKeyPairResponse.data.appKeyPairs) {
      if (appKeyPair.role === Constants.APP_KEY_PAIR_ROLE_AUTHORISATION) {
        appKeyPairId = appKeyPair.appKeyPairId;
      }
    }

    if (appPrivateKey == null || appPrivateKey == "") {
      failureCallback(BayunError.ERROR_INVALID_KEY_PAIR);
      return;
    }
  } catch (error) {
    console.log(error);
    throw getErrorMessage(null, error?.response?.data ?? error);
  }

  const nonAuthRestClient = getNonAuthRestClient(appId, appKeyPairId);

  let signedMemberPublickeyWithMetadata = "";

  try {
    const getMemberPublicKeyResponse = await nonAuthRestClient.post(
      "/memberPublicKeyForAuthorization",
      {
        authorizationAppKeyPairId: appKeyPairId,
        appId,
        orgName,
        orgMemberId,
      },
    );

    await logApiResponse(
      "getMemberPublicKeyForAuthorizationResponse",
      getMemberPublicKeyResponse,
    );

    const signedMemberPublicKey =
      getMemberPublicKeyResponse.data.memberPublicKey;

    if (!signedMemberPublicKey) {
      throw BayunError.ERROR_SOMETHING_WENT_WRONG;
    }

    // Append auth layer onto the existing chain (member + creation-app).
    // Same signing as createMember when the auth key pair is present at register.
    // Signing tag, not the legacy one: appPrivateKey here is
    // AUTHORISATION_APP_SIGNING_PRIVATE_KEY, and AuthorizationAppPublicKeyTag resolves to the
    // app's encryption public key, so that pairing could never verify. The curve and session are
    // required too - without them the curve resolves to undefined and signing fails closed.
    signedMemberPublickeyWithMetadata =
      await signDataAndAppendSignatureWithMetadata(
        signedMemberPublicKey,
        appPrivateKey,
        await getMemberPublicKeyContext(orgName, orgMemberId),
        SigningPublicKeyTags.AUTHORIZATION_APP_SIGNING_PUBLIC_KEY_TAG,
        await getSigningCurveFromStorage(sessionId),
        sessionId,
      );

    if (!signedMemberPublickeyWithMetadata) {
      console.log("MemberPublicKey Signature could not be generated.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
  } catch (error) {
    console.log(error);
    throw getErrorMessage(null, error?.response?.data ?? error);
  }

  try {
    // call the authorizeMember API
    const authorizeMemberResponse = await nonAuthRestClient.post(
      "/authorizeMember",
      {
        authorizationAppKeyPairId: appKeyPairId,
        orgMemberId,
        orgName,
        memberPublicKey: signedMemberPublickeyWithMetadata,
      },
    );

    // console.log(authorizeMemberResponse);
    await logApiResponse("authorizeMemberResponse", authorizeMemberResponse);

    successCallback({
      securityQuestions: null,
      authenticationResponse: AuthenticateResponse.SUCCESS,
      message: MEMBER_AUTHORIZED_RELOGIN_MESSAGE,
    });
  } catch (error) {
    console.log(error);
    failureCallback(error?.response?.data ?? error);
    throw getErrorMessage(null, error?.response?.data ?? error);
  }
};